import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard, LogOut, Download, Cake, TrendingUp, Users, Wallet, Receipt,
  Loader2, Phone, Mail, CalendarDays, ShieldCheck, MessageCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { getAdminStats, getStatsRange, getBirthdays, downloadCsv, getPaymentChanges, approvePaymentChange, rejectPaymentChange, apiError } from "../lib/api";
import { waLinkE164 } from "../lib/events";
import { BrandMark } from "../components/Brand";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { EventsTab } from "../components/EventsTab";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const PERIODS = ["daily", "weekly", "monthly"];
const BDAY_FILTERS = ["all", "this_month", "next_month", "next_30", "select_month"];
const FILTER_LABELS = {
  all: "allBirthdays", this_month: "thisMonth", next_month: "nextMonth",
  next_30: "next30", select_month: "selectMonth",
};
const MONTHS = ["january", "february", "march", "april", "may", "june",
  "july", "august", "september", "october", "november", "december"];

function ConsentBadge({ status, t }) {
  const map = {
    granted: { label: t("consentYes"), cls: "bg-[#26422A]/15 text-[#26422A]" },
    not_granted: { label: t("consentNo"), cls: "bg-[#EA672D]/15 text-[#EA672D]" },
  };
  const s = map[status] || { label: t("consentNotCollected"), cls: "bg-[#5D372A]/10 text-[#5D372A]" };
  return <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${s.cls}`}>{s.label}</span>;
}

function StatCard({ icon: Icon, label, value, accent, testid }) {  return (
    <div className="rounded-2xl border-2 border-[#26422A]/25 bg-white p-4" data-testid={testid}>
      <Icon className={`mb-2 h-5 w-5 ${accent ? "text-[#EA672D]" : "text-[#26422A]"}`} />
      <div className="font-display text-2xl font-black text-[#26422A]">{value}</div>
      <div className="text-xs font-bold text-[#26422A]/80">{label}</div>
    </div>
  );
}

function PeriodStats({ title, label, data, t }) {
  return (
    <div className="rounded-3xl border-2 border-[#26422A]/30 bg-[#FFFDF4] p-5" data-testid={`period-block-${title}`}>
      <h3 className="mb-4 font-display text-xl font-bold text-[#26422A]">{label || t(title)}</h3>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <StatCard icon={TrendingUp} label={t("checkinsMetric")} value={data.check_ins} testid={`${title}-checkins`} />
        <StatCard icon={Users} label={t("childrenMetric")} value={data.num_children} testid={`${title}-children`} />
        <StatCard icon={Wallet} label={t("revenueMetric")} value={`₺${data.revenue}`} testid={`${title}-revenue`} />
        <StatCard icon={Receipt} label={t("transactionsMetric")} value={data.transactions} testid={`${title}-transactions`} />
        <StatCard icon={Wallet} label={t("avgTransaction")} value={`₺${data.avg_transaction}`} accent testid={`${title}-avg`} />
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { t } = useLang();
  const navigate = useNavigate();
  const { auth, logout } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [paymentChanges, setPaymentChanges] = useState([]);

  const handleApproval = async (id, approve) => {
    try {
      if (approve) await approvePaymentChange(auth, id);
      else await rejectPaymentChange(auth, id);
      toast.success(approve ? t("approve") : t("reject"));
      loadStats();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const [bdayFilter, setBdayFilter] = useState("all");
  const [bdayMonth, setBdayMonth] = useState(String(new Date().getMonth() + 1));
  const [bdayLvStart, setBdayLvStart] = useState("");
  const [bdayLvEnd, setBdayLvEnd] = useState("");
  const [bdayEligibleOnly, setBdayEligibleOnly] = useState(false);
  const [birthdays, setBirthdays] = useState(null);
  const [loadingBdays, setLoadingBdays] = useState(false);
  const [busy, setBusy] = useState(null);
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [ovStart, setOvStart] = useState("");
  const [ovEnd, setOvEnd] = useState("");
  const [ovStats, setOvStats] = useState(null);
  const [ovLoading, setOvLoading] = useState(false);

  useEffect(() => {
    if (!auth) navigate("/admin/login");
  }, [auth, navigate]);

  const loadStats = useCallback(async () => {
    if (!auth) return;
    setLoadingStats(true);
    try {
      setStats(await getAdminStats(auth));
      try { setPaymentChanges(await getPaymentChanges(auth)); } catch (_) {}
    } catch (e) {
      if (e?.response?.status === 401) {
        logout();
        navigate("/admin/login");
        return;
      }
      toast.error(apiError(e));
    } finally {
      setLoadingStats(false);
    }
  }, [auth, logout, navigate]);

  const loadBirthdays = useCallback(async () => {
    if (!auth) return;
    setLoadingBdays(true);
    setBirthdays(null);
    try {
      const month = bdayFilter === "select_month" ? bdayMonth : null;
      setBirthdays(await getBirthdays(auth, bdayFilter, month, bdayLvStart, bdayLvEnd, bdayEligibleOnly));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoadingBdays(false);
    }
  }, [auth, bdayFilter, bdayMonth, bdayLvStart, bdayLvEnd, bdayEligibleOnly]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { if (tab === "birthdays") loadBirthdays(); }, [tab, loadBirthdays]);

  const doDownload = async (key, path, params, fallback) => {
    setBusy(key);
    try {
      await downloadCsv(path, auth, params, fallback);
      toast.success(t("downloadCsv"));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  const applyOvRange = async () => {
    if (!ovStart || !ovEnd) return toast.error(t("selectDates"));
    setOvLoading(true);
    try {
      setOvStats(await getStatsRange(auth, ovStart, ovEnd));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setOvLoading(false);
    }
  };

  if (!auth) return null;

  return (
    <div className="min-h-screen bg-[#FFEEBC]" data-testid="admin-dashboard-page">
      <header className="sticky top-0 z-20 border-b border-[#26422A]/30 bg-[#26422A]">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3 sm:py-4">
          <div className="flex min-w-0 flex-1 items-center gap-2.5 sm:gap-3">
            <BrandMark size="sm" className="h-10 w-10 shrink-0 md:h-11 md:w-11" />
            <div className="min-w-0 leading-tight">
              <div className="flex items-center gap-1.5 font-display text-base font-bold text-[#FFEEBC] sm:text-lg">
                <LayoutDashboard className="h-4 w-4 shrink-0" />
                <span className="truncate">{t("adminDashboard")}</span>
              </div>
              <div className="truncate text-xs text-[#FFEEBC]/60">{t("adminSubtitle")}</div>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <LanguageSwitcher />
            <button
              data-testid="admin-logout-btn"
              onClick={() => { logout(); navigate("/"); }}
              aria-label={t("logout")}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/10 text-[#FFEEBC] transition-colors hover:bg-white/20"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 pt-6 pb-24 sm:pb-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid h-auto w-full grid-cols-2 gap-1 rounded-xl bg-white p-1 sm:grid-cols-4">
            <TabsTrigger data-testid="admin-tab-overview" value="overview" className="rounded-lg py-2.5 font-bold data-[state=active]:bg-[#26422A] data-[state=active]:text-[#FFEEBC]">
              {t("overview")}
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-bookkeeping" value="bookkeeping" className="rounded-lg py-2.5 font-bold data-[state=active]:bg-[#26422A] data-[state=active]:text-[#FFEEBC]">
              {t("bookkeeping")}
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-birthdays" value="birthdays" className="rounded-lg py-2.5 font-bold data-[state=active]:bg-[#26422A] data-[state=active]:text-[#FFEEBC]">
              {t("birthdays")}
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-events" value="events" className="rounded-lg py-2.5 font-bold data-[state=active]:bg-[#26422A] data-[state=active]:text-[#FFEEBC]">
              {t("events")}
            </TabsTrigger>
          </TabsList>

          {/* OVERVIEW */}
          <TabsContent value="overview" className="mt-5 space-y-4" data-testid="admin-overview">
            {paymentChanges.length > 0 && (
              <div className="rounded-2xl border-2 border-[#EA672D]/50 bg-[#FFFFFF] p-4" data-testid="payment-approvals">
                <div className="mb-3 font-display text-lg font-bold text-[#26422A]">
                  {t("paymentApprovals")} ({paymentChanges.length})
                </div>
                <div className="space-y-2">
                  {paymentChanges.map((r) => (
                    <div key={r.id} data-testid={`approval-${r.id}`} className="rounded-xl bg-[#FFEEBC] p-3">
                      <div className="text-sm font-bold text-[#26422A]">{r.child_name} · {r.parent_name}</div>
                      <div className="text-xs text-[#5D372A]">
                        {r.action === "amount" && `${t("changeAmount")}: ₺${r.old_amount} → ₺${r.new_amount}`}
                        {r.action === "type" && `${t("changeType")}: ${t(r.old_type || "other")} → ${t(r.new_type || "other")}`}
                        {r.action === "refund" && `${t("refundFull")}: ₺${r.old_amount}`}
                      </div>
                      <div className="mt-2 flex gap-2">
                        <button data-testid={`approve-${r.id}`} onClick={() => handleApproval(r.id, true)} className="h-9 flex-1 rounded-lg bg-[#26422A] text-sm font-bold text-[#FFEEBC]">{t("approve")}</button>
                        <button data-testid={`reject-${r.id}`} onClick={() => handleApproval(r.id, false)} className="h-9 flex-1 rounded-lg border-2 border-[#5D372A] text-sm font-bold text-[#5D372A]">{t("reject")}</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {loadingStats || !stats ? (
              <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[#26422A]" /></div>
            ) : (
              PERIODS.map((p) => <PeriodStats key={p} title={p} data={stats[p]} t={t} />)
            )}

            <div className="rounded-3xl border-2 border-dashed border-[#26422A]/30 bg-white p-5" data-testid="overview-range-box">
              <div className="mb-3 flex items-center gap-2 font-display text-lg font-bold text-[#26422A]">
                <CalendarDays className="h-5 w-5 text-[#EA672D]" /> {t("customRange")}
              </div>
              <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:items-end">
                <div className="min-w-0 space-y-1">
                  <Label className="text-xs font-bold text-[#26422A]">{t("startDate")}</Label>
                  <Input
                    data-testid="overview-range-start"
                    type="date"
                    value={ovStart}
                    max={ovEnd || undefined}
                    onChange={(e) => setOvStart(e.target.value)}
                    className="h-11 w-full min-w-0 rounded-xl border-[#26422A]/40 bg-white font-semibold text-[#26422A] sm:w-[170px]"
                  />
                </div>
                <div className="min-w-0 space-y-1">
                  <Label className="text-xs font-bold text-[#26422A]">{t("endDate")}</Label>
                  <Input
                    data-testid="overview-range-end"
                    type="date"
                    value={ovEnd}
                    min={ovStart || undefined}
                    onChange={(e) => setOvEnd(e.target.value)}
                    className="h-11 w-full min-w-0 rounded-xl border-[#26422A]/40 bg-white font-semibold text-[#26422A] sm:w-[170px]"
                  />
                </div>
                <Button
                  data-testid="overview-range-apply"
                  onClick={applyOvRange}
                  disabled={ovLoading || !ovStart || !ovEnd}
                  className="col-span-2 h-11 w-full gap-2 rounded-xl bg-[#26422A] font-bold text-[#FFEEBC] hover:bg-[#1B3320] sm:w-auto"
                >
                  {ovLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <TrendingUp className="h-4 w-4" />}
                  {t("apply")}
                </Button>
              </div>
            </div>

            {ovStats && (
              <PeriodStats title="range" label={`${ovStart} → ${ovEnd}`} data={ovStats} t={t} />
            )}
          </TabsContent>

          {/* BOOKKEEPING */}
          <TabsContent value="bookkeeping" className="mt-5" data-testid="admin-bookkeeping">
            <div className="rounded-3xl border-2 border-[#26422A]/30 bg-white p-6">
              <div className="mb-1 flex items-center gap-2 font-display text-xl font-bold text-[#26422A]">
                <Receipt className="h-5 w-5" /> {t("bookkeepingCsv")}
              </div>
              <p className="mb-5 text-sm text-[#26422A]/80">{t("bookkeepingDesc")}</p>
              <div className="grid gap-3 sm:grid-cols-3">
                {PERIODS.map((p) => (
                  <Button
                    key={p}
                    data-testid={`download-bookkeeping-${p}`}
                    onClick={() => doDownload(`bk-${p}`, "/admin/bookkeeping.csv", { period: p }, `bookkeeping-${p}.csv`)}
                    disabled={busy === `bk-${p}`}
                    className="h-14 justify-start gap-3 rounded-xl bg-[#26422A] font-bold text-[#FFEEBC] hover:bg-[#1B3320]"
                  >
                    {busy === `bk-${p}` ? <Loader2 className="h-5 w-5 animate-spin" /> : <Download className="h-5 w-5" />}
                    {t(p)}
                  </Button>
                ))}
              </div>

              <div className="mt-6 rounded-2xl border-2 border-dashed border-[#26422A]/30 bg-[#FFFDF4] p-4" data-testid="custom-range-box">
                <div className="mb-3 flex items-center gap-2 font-display text-lg font-bold text-[#26422A]">
                  <CalendarDays className="h-5 w-5 text-[#EA672D]" /> {t("customRange")}
                </div>
                <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:items-end">
                  <div className="min-w-0 space-y-1">
                    <Label className="text-xs font-bold text-[#26422A]">{t("startDate")}</Label>
                    <Input
                      data-testid="range-start-input"
                      type="date"
                      value={rangeStart}
                      max={rangeEnd || undefined}
                      onChange={(e) => setRangeStart(e.target.value)}
                      className="h-11 w-full min-w-0 rounded-xl border-[#26422A]/40 bg-white font-semibold text-[#26422A] sm:w-[170px]"
                    />
                  </div>
                  <div className="min-w-0 space-y-1">
                    <Label className="text-xs font-bold text-[#26422A]">{t("endDate")}</Label>
                    <Input
                      data-testid="range-end-input"
                      type="date"
                      value={rangeEnd}
                      min={rangeStart || undefined}
                      onChange={(e) => setRangeEnd(e.target.value)}
                      className="h-11 w-full min-w-0 rounded-xl border-[#26422A]/40 bg-white font-semibold text-[#26422A] sm:w-[170px]"
                    />
                  </div>
                  <Button
                    data-testid="download-bookkeeping-range"
                    onClick={() => {
                      if (!rangeStart || !rangeEnd) return toast.error(t("selectDates"));
                      doDownload("bk-range", "/admin/bookkeeping.csv",
                        { start: rangeStart, end: rangeEnd }, `bookkeeping-${rangeStart}_to_${rangeEnd}.csv`);
                    }}
                    disabled={busy === "bk-range" || !rangeStart || !rangeEnd}
                    className="col-span-2 h-11 w-full gap-2 rounded-xl bg-[#EA672D] font-bold text-white hover:bg-[#d0561f] sm:w-auto"
                  >
                    {busy === "bk-range" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                    {t("downloadRange")}
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* BIRTHDAYS */}
          <TabsContent value="birthdays" className="mt-5 space-y-4" data-testid="admin-birthdays">
            <div className="flex flex-col gap-3 rounded-3xl border-2 border-[#26422A]/30 bg-white p-4">
              <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 font-display text-lg font-bold text-[#26422A]">
                <Cake className="h-5 w-5 text-[#EA672D]" /> {t("birthdays")}
              </div>
              <div className="flex w-full flex-wrap items-center gap-2 sm:ml-auto sm:w-auto">
                <Select value={bdayFilter} onValueChange={setBdayFilter}>
                  <SelectTrigger data-testid="birthday-filter" className="h-11 w-full rounded-xl border-[#26422A]/40 font-semibold text-[#26422A] sm:w-[170px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BDAY_FILTERS.map((f) => (
                      <SelectItem key={f} value={f} data-testid={`bfilter-${f}`}>{t(FILTER_LABELS[f])}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {bdayFilter === "select_month" && (
                  <Select value={bdayMonth} onValueChange={setBdayMonth}>
                    <SelectTrigger data-testid="birthday-month" className="h-11 w-full rounded-xl border-[#26422A]/40 font-semibold text-[#26422A] sm:w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTHS.map((m, i) => (
                        <SelectItem key={m} value={String(i + 1)} data-testid={`bmonth-${i + 1}`}>{t(m)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Button
                  data-testid="export-birthdays-btn"
                  onClick={() => doDownload("bday", "/admin/birthdays.csv",
                    {
                      filter: bdayFilter,
                      ...(bdayFilter === "select_month" ? { month: bdayMonth } : {}),
                      ...(bdayLvStart ? { last_visit_start: bdayLvStart } : {}),
                      ...(bdayLvEnd ? { last_visit_end: bdayLvEnd } : {}),
                      ...(bdayEligibleOnly ? { eligible_only: true } : {}),
                    },
                    "birthdays.csv")}
                  disabled={busy === "bday" || !birthdays?.count}
                  className="h-11 w-full gap-2 rounded-xl bg-[#EA672D] font-bold text-white hover:bg-[#d0561f] sm:w-auto"
                >
                  {busy === "bday" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  {t("exportBirthdayList")}
                </Button>
              </div>
              </div>
              <div className="flex flex-col gap-3 border-t border-[#26422A]/15 pt-3 sm:flex-row sm:flex-wrap sm:items-end">
                <div className="flex items-center gap-2 font-semibold text-[#26422A]">
                  <CalendarDays className="h-4 w-4 text-[#EA672D]" /> {t("lastVisitRange")}
                </div>
                <div className="grid w-full grid-cols-1 gap-3 sm:flex sm:w-auto sm:items-end">
                  <div className="flex min-w-0 flex-col gap-1">
                    <Label className="text-xs font-bold text-[#26422A]/70">{t("startDate")}</Label>
                    <Input
                      data-testid="bday-lv-start"
                      type="date"
                      value={bdayLvStart}
                      max={bdayLvEnd || undefined}
                      onChange={(e) => setBdayLvStart(e.target.value)}
                      className="h-11 w-full min-w-0 rounded-xl border-[#26422A]/40 font-semibold text-[#26422A] sm:w-[150px]"
                    />
                  </div>
                  <div className="flex min-w-0 flex-col gap-1">
                    <Label className="text-xs font-bold text-[#26422A]/70">{t("endDate")}</Label>
                    <Input
                      data-testid="bday-lv-end"
                      type="date"
                      value={bdayLvEnd}
                      min={bdayLvStart || undefined}
                      onChange={(e) => setBdayLvEnd(e.target.value)}
                      className="h-11 w-full min-w-0 rounded-xl border-[#26422A]/40 font-semibold text-[#26422A] sm:w-[150px]"
                    />
                  </div>
                </div>
                {(bdayLvStart || bdayLvEnd) && (
                  <Button
                    data-testid="bday-lv-clear"
                    variant="outline"
                    onClick={() => { setBdayLvStart(""); setBdayLvEnd(""); }}
                    className="h-11 w-full rounded-xl border-[#26422A]/40 font-bold text-[#26422A] sm:w-auto"
                  >
                    {t("clearRange")}
                  </Button>
                )}
                <button
                  type="button"
                  data-testid="bday-eligible-toggle"
                  aria-pressed={bdayEligibleOnly}
                  onClick={() => setBdayEligibleOnly((v) => !v)}
                  className={`inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl border-2 px-4 text-sm font-bold transition-colors sm:ml-auto sm:w-auto ${
                    bdayEligibleOnly
                      ? "border-[#26422A] bg-[#26422A] text-[#FFEEBC]"
                      : "border-[#26422A]/40 bg-white text-[#26422A] hover:bg-[#26422A]/5"
                  }`}
                >
                  <ShieldCheck className="h-4 w-4" /> {t("eligibleOnly")}
                </button>
              </div>
            </div>

            <p className="px-1 text-xs text-[#5D372A]">{t("emailNotCollected")}</p>

            {loadingBdays ? (
              <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[#26422A]" /></div>
            ) : !birthdays || birthdays.count === 0 ? (
              <div className="rounded-3xl border-2 border-dashed border-[#26422A]/30 bg-white/60 py-16 text-center" data-testid="no-birthdays">
                <Cake className="mx-auto mb-3 h-10 w-10 text-[#26422A]/40" />
                <p className="font-bold text-[#26422A]">{t("noBirthdays")}</p>
              </div>
            ) : (
              <>
                {/* Mobile: card list */}
                <div className="space-y-3 lg:hidden" data-testid="birthdays-cards">
                  <p className="px-1 text-xs font-bold text-[#26422A]/70">{birthdays.count} {t("birthdays")}</p>
                  {birthdays.children.map((c, i) => (
                    <motion.div
                      key={c.child_id}
                      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
                      className="rounded-2xl border-2 border-[#26422A]/25 bg-white p-4"
                      data-testid={`birthday-card-${i}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-display text-lg font-bold leading-tight text-[#26422A]">{c.child_first_name}</div>
                          <div className="mt-1 inline-flex items-center gap-1.5 text-sm text-[#26422A]">
                            <CalendarDays className="h-3.5 w-3.5 text-[#EA672D]" /> {c.birthday}
                          </div>
                        </div>
                        {c.turning_age != null && (
                          <span className="shrink-0 rounded-full bg-[#EA672D]/15 px-2.5 py-1 text-sm font-bold text-[#EA672D]">
                            {t("turningAge")}: {c.turning_age}
                          </span>
                        )}
                      </div>

                      <div className="mt-3 space-y-1.5 text-sm text-[#26422A]">
                        <div className="flex items-start justify-between gap-3">
                          <span className="text-[#26422A]/60">{t("guardianName")}</span>
                          <span className="text-right font-semibold">{c.parent_name || "--"}</span>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-[#26422A]/60">{t("phone")}</span>
                          <span className="flex items-center justify-end gap-2 text-right font-semibold">
                            {c.parent_phone || "--"}
                            {c.parent_phone && (
                              <a
                                data-testid={`bday-whatsapp-${i}`}
                                href={waLinkE164(c.parent_phone)}
                                target="_blank"
                                rel="noreferrer"
                                aria-label="WhatsApp"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#25D366] text-white transition-transform hover:scale-105"
                              >
                                <MessageCircle className="h-4 w-4" />
                              </a>
                            )}
                          </span>
                        </div>
                        <div className="flex items-start justify-between gap-3">
                          <span className="text-[#26422A]/60">{t("lastVisit")}</span>
                          <span className="text-right font-semibold">{c.last_visit || "--"}</span>
                        </div>
                      </div>

                      <div className="mt-3 grid grid-cols-2 gap-3 border-t border-[#26422A]/10 pt-3">
                        <div>
                          <div className="text-xs text-[#26422A]/60">{t("colMarketingConsent")}</div>
                          <div className="mt-1"><ConsentBadge status={c.marketing_data_consent} t={t} /></div>
                          {c.marketing_data_consent_date && (
                            <div className="mt-1 text-xs text-[#26422A]/70">{c.marketing_data_consent_date}</div>
                          )}
                        </div>
                        <div>
                          <div className="text-xs text-[#26422A]/60">{t("colCommConsent")}</div>
                          <div className="mt-1"><ConsentBadge status={c.commercial_message_consent} t={t} /></div>
                          {c.commercial_message_consent_date && (
                            <div className="mt-1 text-xs text-[#26422A]/70">{c.commercial_message_consent_date}</div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Desktop/tablet: table */}
                <div className="hidden overflow-hidden rounded-3xl border-2 border-[#26422A]/30 bg-white lg:block" data-testid="birthdays-table">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                      <thead className="bg-[#26422A] text-[#FFEEBC]">
                        <tr>
                          <th className="px-4 py-3 font-bold">{t("childFirstName")}</th>
                          <th className="px-4 py-3 font-bold">{t("birthday")}</th>
                          <th className="px-4 py-3 font-bold">{t("turningAge")}</th>
                          <th className="px-4 py-3 font-bold">{t("guardianName")}</th>
                          <th className="px-4 py-3 font-bold">{t("phone")}</th>
                          <th className="px-4 py-3 font-bold">{t("lastVisit")}</th>
                          <th className="px-4 py-3 font-bold">{t("colMarketingConsent")}</th>
                          <th className="px-4 py-3 font-bold">{t("colMarketingConsentDate")}</th>
                          <th className="px-4 py-3 font-bold">{t("colCommConsent")}</th>
                          <th className="px-4 py-3 font-bold">{t("colCommConsentDate")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {birthdays.children.map((c, i) => (
                          <motion.tr
                            key={c.child_id}
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }}
                            className="border-t border-[#26422A]/15 text-[#26422A]"
                            data-testid={`birthday-row-${i}`}
                          >
                            <td className="px-4 py-3 font-bold">{c.child_first_name}</td>
                            <td className="px-4 py-3">
                              <span className="inline-flex items-center gap-1.5">
                                <CalendarDays className="h-3.5 w-3.5 text-[#EA672D]" /> {c.birthday}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className="rounded-full bg-[#EA672D]/15 px-2 py-0.5 font-bold text-[#EA672D]">{c.turning_age}</span>
                            </td>
                            <td className="px-4 py-3">{c.parent_name || "--"}</td>
                            <td className="px-4 py-3">
                              <span className="inline-flex items-center gap-2">
                                <Phone className="h-3.5 w-3.5" /> {c.parent_phone || "--"}
                                {c.parent_phone && (
                                  <a
                                    data-testid={`bday-whatsapp-row-${i}`}
                                    href={waLinkE164(c.parent_phone)}
                                    target="_blank"
                                    rel="noreferrer"
                                    aria-label="WhatsApp"
                                    className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#25D366] text-white transition-transform hover:scale-105"
                                  >
                                    <MessageCircle className="h-3.5 w-3.5" />
                                  </a>
                                )}
                              </span>
                            </td>
                            <td className="px-4 py-3">{c.last_visit || "--"}</td>
                            <td className="px-4 py-3"><ConsentBadge status={c.marketing_data_consent} t={t} /></td>
                            <td className="px-4 py-3">{c.marketing_data_consent_date || "--"}</td>
                            <td className="px-4 py-3"><ConsentBadge status={c.commercial_message_consent} t={t} /></td>
                            <td className="px-4 py-3">{c.commercial_message_consent_date || "--"}</td>
                          </motion.tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </TabsContent>

          {/* EVENTS */}
          <TabsContent value="events" className="mt-5" data-testid="events-tab-content">
            <EventsTab auth={auth} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
